import { Component, OnInit, ViewChild, ElementRef, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators, FormArray } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatTableModule } from '@angular/material/table';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { Destroyable } from '../../utils/mixins/destroyable.mixin';
import { UpscalingRockFile, PhaseInfo } from '../../core/models/upscaling-rock-files.model';
import { RockImagesService } from '../../services/rock-images/rock-images.service';
import { SnackbarService } from '../../services/snackbar.service';
import { LoadingService } from '../../services/loading.service';
import { HttpHeaders } from '@angular/common/http';
import { COMPONENT_ID_HEADER } from '../../core/consts/api.const';
import { SelectPermeabilityPopupComponent } from '../../common/popups/select-permeability-popup/select-permeability-popup.component';
import { SelectFormationFactorPopupComponent } from '../../common/popups/select-formation-factor-popup/select-formation-factor-popup.component';
import { ViewPhasesPopupComponent } from '../../common/popups/view-phases-popup/view-phases-popup.component';
import { RockModelsService } from '../../services/rock-models/rock-models.service';
import { RockThumbnailService } from '../../services/rock-thumbnail/rock-thumbnail.service';
import { UpscalingRockFileModelDto } from '../../core/models/upscaling-rock-file-models.model';
import { concatMap, EMPTY, tap } from 'rxjs';
import { RemoveExtensionPipe } from '../../common/pipes/remove-extension.pipe';

interface PhaseDefinition {
  phase: number;
  label: number;
  fraction: number;
  porosity: number | null;
  coveredPorosity?: number;
  permeability?: any;
  formationFactor?: any;
  color?: string;
}

@Component({
  selector: 'app-phase-definition',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatTableModule,
    MatIconModule,
    MatProgressSpinnerModule,
    RemoveExtensionPipe,
  ],
  templateUrl: './phase-definition.component.html',
  styleUrl: './phase-definition.component.scss',
})
export class PhaseDefinitionComponent extends Destroyable(Object) implements OnInit {
  @ViewChild('porosityInput') porosityInput!: ElementRef<HTMLInputElement>;

  private readonly componentId: string = 'phase-definition';
  isLoading = true;
  fileName: string = '';
  modelId: string | null = null;
  sourceModelId: string | null = null;
  isReadonly: boolean = false;
  rockFile: UpscalingRockFile | null = null;
  thumbnailUrl: string | null = null;
  editingPhase: PhaseDefinition | null = null;

  rockModelNameForm: FormGroup;
  phaseDefinitions: PhaseDefinition[] = [];

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly fb: FormBuilder,
    private readonly dialog: MatDialog,
    private readonly rockImagesService: RockImagesService,
    private readonly rockModelsService: RockModelsService,
    private readonly rockThumbnailService: RockThumbnailService,
    private readonly snackbarService: SnackbarService,
    private readonly loadingService: LoadingService,
    private readonly cdr: ChangeDetectorRef,
  ) {
    super();
    this.rockModelNameForm = this.fb.group({
      name: ['', [Validators.required, Validators.maxLength(256), Validators.pattern(/^[a-zA-Z0-9_-]+$/)]]
    });
  }

  ngOnInit(): void {
    this.subscribeToLoadingState();
    this.route.queryParams.pipe(this.takeUntilDestroyed()).subscribe((params) => {
      this.fileName = params['fileName'] || '';
      this.modelId = params['modelId'] || null;
      this.sourceModelId = params['sourceModelId'] || null;
      this.isReadonly = !!this.modelId;
      
      if (this.fileName) {
        if (this.modelId) {
          this.loadRockModel();
        } else if (this.sourceModelId) {
          this.loadRockModelForDuplicate();
        } else {
          this.loadRockFileMetadata();
        }
      } else {
        this.router.navigate(['/upscaling/rock-images']);
      }
    });
  }

  private loadRockModel(): void {
    if (!this.modelId) return;

    this.loadingService.startLoading(this.componentId);

    const options = {
      headers: new HttpHeaders({
        [COMPONENT_ID_HEADER]: this.componentId,
      }),
    };

    this.rockImagesService
      .getRockFileMetadata(this.fileName, 'Upscaling')
      .pipe(
        tap((metadata) => {
          const upscalingFile = metadata as UpscalingRockFile;
          this.rockFile = upscalingFile;
          this.loadThumbnail();
        }),
        concatMap(() =>
          this.rockModelsService.getUpscalingRockFileModel(this.fileName, this.modelId!, options)
        ),
        tap((model: UpscalingRockFileModelDto) => {
          this.rockModelNameForm.patchValue({ name: model.name });
          this.rockModelNameForm.disable();

          this.phaseDefinitions = model.phasesDefinition.map((phase: any) => {
            const phaseNumber = (phase as any).phaseNumber !== undefined ? (phase as any).phaseNumber : phase.phase;
            const label = phase.label !== undefined && phase.label !== null ? phase.label : phaseNumber;
            const phaseValue = phase.phase !== undefined && phase.phase !== null ? phase.phase : phaseNumber;
            const color = this.calculatePhaseColor(label);
            const coveredPorosity = phase.coveredPorosity !== undefined && phase.coveredPorosity !== null
              ? phase.coveredPorosity
              : 1;
            return {
              phase: phaseValue,
              label: label,
              fraction: phase.fraction,
              porosity: phase.porosity,
              coveredPorosity,
              permeability: phase.permeability,
              formationFactor: phase.formationFactor,
              color: color,
            };
          });

          this.loadingService.stopLoading(this.componentId);
        }),
        this.takeUntilDestroyed(),
      )
      .subscribe();
  }

  private loadRockModelForDuplicate(): void {
    if (!this.sourceModelId) return;

    this.loadingService.startLoading(this.componentId);

    const options = {
      headers: new HttpHeaders({
        [COMPONENT_ID_HEADER]: this.componentId,
      }),
    };

    this.rockImagesService
      .getRockFileMetadata(this.fileName, 'Upscaling')
      .pipe(
        tap((metadata) => {
          const upscalingFile = metadata as UpscalingRockFile;
          this.rockFile = upscalingFile;
          this.loadThumbnail();
        }),
        concatMap(() =>
          this.rockModelsService.getUpscalingRockFileModel(this.fileName, this.sourceModelId!, options)
        ),
        tap((model: UpscalingRockFileModelDto) => {
          const duplicateName = `${model.name}_duplicate`;
          this.rockModelNameForm.enable();
          this.rockModelNameForm.patchValue({ name: duplicateName });

          this.phaseDefinitions = model.phasesDefinition.map((phase: any) => {
            const phaseNumber = (phase as any).phaseNumber !== undefined ? (phase as any).phaseNumber : phase.phase;
            const label = phase.label !== undefined && phase.label !== null ? phase.label : phaseNumber;
            const phaseValue = phase.phase !== undefined && phase.phase !== null ? phase.phase : phaseNumber;
            const color = this.calculatePhaseColor(label);
            const coveredPorosity = phase.coveredPorosity !== undefined && phase.coveredPorosity !== null
              ? phase.coveredPorosity
              : 1;
            return {
              phase: phaseValue,
              label: label,
              fraction: phase.fraction,
              porosity: phase.porosity,
              coveredPorosity,
              permeability: phase.permeability,
              formationFactor: phase.formationFactor,
              color: color,
            };
          });

          this.loadingService.stopLoading(this.componentId);
        }),
        this.takeUntilDestroyed(),
      )
      .subscribe();
  }

  private loadRockFileMetadata(): void {
    this.loadingService.startLoading(this.componentId);

    const options = {
      headers: new HttpHeaders({
        [COMPONENT_ID_HEADER]: this.componentId,
      }),
    };

    this.rockImagesService.getRockFileMetadata(this.fileName, 'Upscaling')
      .pipe(this.takeUntilDestroyed())
      .subscribe({
        next: (metadata) => {
          const upscalingFile = metadata as UpscalingRockFile;
          this.rockFile = upscalingFile;
          this.initializePhaseDefinitions();
          this.loadThumbnail();
          this.loadingService.stopLoading(this.componentId);
        }
      });
  }

  private loadThumbnail(): void {
    this.rockThumbnailService.getThumbnail(this.fileName, 'Upscaling')
      .pipe(this.takeUntilDestroyed())
      .subscribe({
        next: (url) => {
          this.thumbnailUrl = url;
        }
      });
  }

  private initializePhaseDefinitions(): void {
    if (!this.rockFile) return;

    if (this.rockFile.fractionHistogram) {
      this.phaseDefinitions = Object.entries(this.rockFile.fractionHistogram).map(([label, fraction], index) => {
        const labelNum = parseInt(label, 10);
        const color = this.calculatePhaseColor(labelNum);
        return {
          phase: index,
          label: labelNum,
          fraction: fraction,
          porosity: null,
          coveredPorosity: 1,
          color: color,
        };
      });
    } else if (this.rockFile.phases) {
      this.phaseDefinitions = this.rockFile.phases.map(phase => {
        const color = this.calculatePhaseColor(phase.label);
        return {
          ...phase,
          porosity: null,
          coveredPorosity: 1,
          color: color,
        };
      });
    }
  }

  private calculatePhaseColor(label: number | null | undefined): string {
    if (label === null || label === undefined || isNaN(label)) {
      return 'rgb(128, 128, 128)';
    }

    if (!this.rockFile) {
      return 'rgb(128, 128, 128)';
    }

    const labelStr = String(label);
    
    if (this.rockFile.normalizedPhases && this.rockFile.normalizedPhases[labelStr] !== undefined) {
      const normalizedValue = this.rockFile.normalizedPhases[labelStr];
      if (normalizedValue !== null && normalizedValue !== undefined && !isNaN(normalizedValue)) {
        return `rgb(${normalizedValue}, ${normalizedValue}, ${normalizedValue})`;
      }
    }
    
    if (this.rockFile.maxPhaseValue && this.rockFile.maxPhaseValue > 0) {
      const grayValue = Math.round((Number(label) / this.rockFile.maxPhaseValue) * 255);
      const clampedValue = Math.max(0, Math.min(255, grayValue));
      return `rgb(${clampedValue}, ${clampedValue}, ${clampedValue})`;
    }

    return 'rgb(128, 128, 128)';
  }

  isFieldDisabled(phaseDefinition: PhaseDefinition): boolean {
    return phaseDefinition.porosity === 0 || phaseDefinition.porosity === 1;
  }

  startEditingPorosity(phase: PhaseDefinition): void {
    if (this.isReadonly) return;
    this.editingPhase = phase;
    setTimeout(() => {
      if (this.porosityInput) {
        this.porosityInput.nativeElement.focus();
        this.porosityInput.nativeElement.select();
      }
    }, 0);
  }

  onPorosityBlur(phase: PhaseDefinition): void {
    const v = phase.porosity;
    if (v === null || v === undefined || Number.isNaN(v as any)) {
      phase.porosity = null;
    } else if (v < 0) {
      phase.porosity = 0;
      this.snackbarService.openCustom({
        message: 'Porosity must be between 0 and 1',
        title: 'Validation Error',
        type: 'error',
        action: 'Close',
      });
    } else if (v > 1) {
      phase.porosity = 1;
      this.snackbarService.openCustom({
        message: 'Porosity must be between 0 and 1',
        title: 'Validation Error',
        type: 'error',
        action: 'Close',
      });
    }

    this.onPorosityChange(phase);
  }

  onPorosityChange(phase: PhaseDefinition): void {
    if (this.isFieldDisabled(phase)) {
      phase.permeability = undefined;
      phase.formationFactor = undefined;
      phase.coveredPorosity = 1;
    }
  }

  onCoveredPorosityBlur(phase: PhaseDefinition): void {
    if (this.isFieldDisabled(phase)) return;
    const v = phase.coveredPorosity;
    if (v === null || v === undefined || Number.isNaN(v as number)) {
      phase.coveredPorosity = 1;
    } else if (v <= 0) {
      phase.coveredPorosity = 1;
      this.snackbarService.openCustom({
        message: 'Covered Porosity must be greater than 0 and at most 1',
        title: 'Validation Error',
        type: 'error',
        action: 'Close',
      });
    } else if (v > 1) {
      phase.coveredPorosity = 1;
      this.snackbarService.openCustom({
        message: 'Covered Porosity must be greater than 0 and at most 1',
        title: 'Validation Error',
        type: 'error',
        action: 'Close',
      });
    }
  }

  selectPermeability(phase: PhaseDefinition): void {
    if (this.isReadonly || this.isFieldDisabled(phase)) return;

    const dialogData: any = { fileName: this.fileName };
    if (phase.permeability?.simulationId) {
      dialogData.preselection = {
        simulationId: phase.permeability.simulationId,
        direction: phase.permeability.direction,
        cubeSizeVoxels: phase.permeability.cubeSizeVoxels || phase.permeability.cubeSize,
      };
    }

    const dialogRef = this.dialog.open(SelectPermeabilityPopupComponent, {
      width: '95vw',
      maxWidth: '1400px',
      minWidth: '1100px',
      maxHeight: '90vh',
      data: dialogData,
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        phase.permeability = {
          simulationId: result.simulationId,
          simulationName: result.simulationName,
          direction: result.direction,
          cubeSize: result.cubeSize,
          cubeSizeVoxels: result.cubeSizeVoxels,
          cubeSizeFraction: result.cubeSizeFraction,
          angularCoefficient: result.angularCoefficient,
          exponentialCoefficient: result.exponentialCoefficient,
        };
        this.phaseDefinitions = [...this.phaseDefinitions];
        this.cdr.detectChanges();
      }
    });
  }

  selectFormationFactor(phase: PhaseDefinition): void {
    if (this.isReadonly || this.isFieldDisabled(phase)) return;

    const dialogData: any = { fileName: this.fileName };
    if (phase.formationFactor?.simulationId) {
      dialogData.preselection = {
        simulationId: phase.formationFactor.simulationId,
        direction: phase.formationFactor.direction,
        cubeSizeVoxels: phase.formationFactor.cubeSizeVoxels || phase.formationFactor.cubeSize,
      };
    }

    const dialogRef = this.dialog.open(SelectFormationFactorPopupComponent, {
      width: '95vw',
      maxWidth: '1400px',
      minWidth: '1100px',
      maxHeight: '90vh',
      data: dialogData,
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        phase.formationFactor = {
          simulationId: result.simulationId,
          simulationName: result.simulationName,
          direction: result.direction,
          cubeSize: result.cubeSize,
          cubeSizeVoxels: result.cubeSizeVoxels,
          cubeSizeFraction: result.cubeSizeFraction,
          angularCoefficient: result.angularCoefficient,
          exponentialCoefficient: result.exponentialCoefficient,
          mValue: result.mValue,
        };
        this.phaseDefinitions = [...this.phaseDefinitions];
        this.cdr.detectChanges();
      }
    });
  }

  canSave(): boolean {
    if (!this.rockModelNameForm.valid) return false;

    if (!this.phaseDefinitions.every(phase => phase.porosity !== null && phase.porosity !== undefined)) return false;
    if (!this.phaseDefinitions.every(phase => (phase.porosity as number) >= 0 && (phase.porosity as number) <= 1)) return false;

    const enabled = this.getEnabledPhasesForSelections();
    const coveredPorosityValid = enabled.every(
      (p) =>
        p.coveredPorosity != null &&
        !Number.isNaN(p.coveredPorosity) &&
        p.coveredPorosity > 0 &&
        p.coveredPorosity <= 1
    );
    if (!coveredPorosityValid) return false;

    const { ok } = this.validateSelections(enabled);
    return ok;
  }

  save(): void {
    if (!this.canSave()) {
      this.snackbarService.openCustom({
        message: 'Please fill in all required fields',
        title: 'Validation Error',
        type: 'error',
        action: 'Close',
      });
      return;
    }

    const rockModelName = (this.rockModelNameForm.value.name || '').toString();

    const totalPorosity = this.phaseDefinitions.reduce(
      (sum, phase) => sum + (phase.porosity || 0) * phase.fraction,
      0
    );

    const enabled = this.getEnabledPhasesForSelections();
    const validation = this.validateSelections(enabled);
    if (!validation.ok) {
      this.snackbarService.openCustom({
        message: validation.message || 'Invalid selection',
        title: 'Validation Error',
        type: 'error',
        action: 'Close',
      });
      return;
    }

    const options = {
      headers: new HttpHeaders({
        [COMPONENT_ID_HEADER]: this.componentId,
      }),
    };

    const payload = {
      name: rockModelName,
      phasesDefinition: this.phaseDefinitions.map((p) => {
        const phaseDef: any = {
          phaseNumber: p.label !== undefined && p.label !== null ? p.label : p.phase,
          porosity: p.porosity,
          coveredPorosity: this.isFieldDisabled(p) ? 1 : (p.coveredPorosity ?? 1),
        };

        if (p.permeability) {
          phaseDef.permeability = {
            simulationId: p.permeability.simulationId,
            simulationName: p.permeability.simulationName,
            direction: p.permeability.direction,
            cubeSizeVoxels: p.permeability.cubeSizeVoxels || p.permeability.cubeSize,
            cubeSizeFraction: p.permeability.cubeSizeFraction || 0,
            angularCoefficient: p.permeability.angularCoefficient,
            exponentialCoefficient: p.permeability.exponentialCoefficient,
          };
        }

        if (p.formationFactor) {
          phaseDef.formationFactor = {
            simulationId: p.formationFactor.simulationId,
            simulationName: p.formationFactor.simulationName,
            direction: p.formationFactor.direction,
            cubeSizeVoxels: p.formationFactor.cubeSizeVoxels || p.formationFactor.cubeSize,
            cubeSizeFraction: p.formationFactor.cubeSizeFraction || 0,
            angularCoefficient: p.formationFactor.angularCoefficient,
            exponentialCoefficient: p.formationFactor.exponentialCoefficient,
          };
        }

        return phaseDef;
      }),
    };

    this.loadingService.startLoading(this.componentId);
    this.rockModelsService
      .createUpscalingRockFileModel(this.fileName, payload, options)
      .pipe(
        tap(() => {
          this.snackbarService.openCustom({
            message: `Rock model "${rockModelName}" saved successfully`,
            title: 'Success',
            type: 'success',
            action: 'Close',
          });
        }),
        concatMap(() => {
          this.loadingService.stopLoading(this.componentId);
          this.router.navigate(['/rock-models'], {
            queryParams: { fileName: this.fileName },
          });
          return EMPTY;
        }),
        this.takeUntilDestroyed(),
      )
      .subscribe();
  }

  cancel(): void {
    this.router.navigate(['/rock-models'], {
      queryParams: { fileName: this.fileName }
    });
  }

  back(): void {
    this.cancel();
  }

  viewPhases(): void {
    if (!this.rockFile) return;

    this.dialog.open(ViewPhasesPopupComponent, {
      maxWidth: '90vw',
      maxHeight: '90vh',
      panelClass: 'stretch-dialog',
      data: {
        phases: this.rockFile.phases || [],
        fileName: this.fileName
      }
    });
  }

  getFileSizeDisplay(): string {
    if (!this.rockFile) return 'N/A';

    const x = this.rockFile.voxelsX || 0;
    const y = this.rockFile.voxelsY || 0;
    const z = this.rockFile.voxelsZ || 0;

    if (x && y && z) {
      return `${x}x${y}x${z}`;
    }
    return 'N/A';
  }

  getPorosityDisplay(phase: PhaseDefinition): string {
    if (phase.porosity === null || phase.porosity === undefined) {
      return 'Manual input';
    }
    if (phase.porosity === 0 || phase.porosity === 1) {
      return phase.porosity.toString();
    }
    return phase.porosity.toString();
  }

  getPermeabilityDisplay(phase: PhaseDefinition): string {
    if (this.isFieldDisabled(phase)) {
      return '-';
    }
    if (phase.permeability) {
      return phase.permeability.simulationName || 'Selected';
    }
    return 'Select Kabs Cube trends';
  }

  getFormationFactorDisplay(phase: PhaseDefinition): string {
    if (this.isFieldDisabled(phase)) {
      return '-';
    }
    if (phase.formationFactor) {
      return phase.formationFactor.simulationName || 'Selected';
    }
    return 'Select FF Cube trends';
  }

  private getEnabledPhasesForSelections(): PhaseDefinition[] {
    return this.phaseDefinitions.filter((p) => p.porosity !== null && p.porosity !== undefined && !this.isFieldDisabled(p));
  }

  private validateSelections(enabled: PhaseDefinition[]): { ok: boolean; message?: string } {
    const anyPerm = enabled.some((p) => !!p.permeability);
    const allPerm = enabled.every((p) => !!p.permeability);
    if (anyPerm && !allPerm) {
      return { ok: false, message: 'Permeability must be selected for all phases (or left blank for all).' };
    }

    const anyFF = enabled.some((p) => !!p.formationFactor);
    const allFF = enabled.every((p) => !!p.formationFactor);
    if (anyFF && !allFF) {
      return { ok: false, message: 'Formation Factor must be selected for all phases (or left blank for all).' };
    }

    if (enabled.length > 0 && !allPerm && !allFF) {
      return { ok: false, message: 'Select at least one: Permeability or Formation Factor.' };
    }

    return { ok: true };
  }

  private subscribeToLoadingState(): void {
    this.loadingService
      .getLoadingState(this.componentId)
      .pipe(this.takeUntilDestroyed())
      .subscribe((loadingState: boolean) => {
        this.isLoading = loadingState;
      });
  }
}
